import { Observable, Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { FormGroup } from '@angular/forms';
import { CustomerService } from '../../services/customer.service';

const PHONE_SEARCH_MIN_LENGTH = 3;
const NAME_SEARCH_MIN_LENGTH = 2;

export interface CustomerPhoneSearchState {
  isSearchingCustomers: boolean;
  phoneSearchResults: any[];
  showPhoneDropdown: boolean;
}

/**
 * Run phone search and update the given state (isSearchingCustomers, phoneSearchResults, showPhoneDropdown).
 * Use in add-customer-modal and customer-info (and similar components) to avoid duplicating search logic.
 */
export function runCustomerPhoneSearch(
  phoneNumber: string,
  customerService: CustomerService,
  destroy$: Observable<void>,
  state: CustomerPhoneSearchState
): void {
  if (!phoneNumber || phoneNumber.trim().length < PHONE_SEARCH_MIN_LENGTH) {
    state.phoneSearchResults = [];
    state.showPhoneDropdown = false;
    return;
  }
  state.isSearchingCustomers = true;
  state.showPhoneDropdown = true;
  customerService.searchByPhone(phoneNumber).pipe(takeUntil(destroy$)).subscribe({
    next: (customers) => {
      state.isSearchingCustomers = false;
      state.phoneSearchResults = customers;
      state.showPhoneDropdown = customers.length > 0;
    },
    error: () => {
      state.isSearchingCustomers = false;
      state.phoneSearchResults = [];
      state.showPhoneDropdown = false;
    }
  });
}

export interface CustomerNameSearchState {
  isSearchingCustomersByName: boolean;
  nameSearchResults: any[];
  showNameDropdown: boolean;
}

/**
 * Run name search and update the given state. Use in add-customer-modal and customer-info.
 */
export function runCustomerNameSearch(
  name: string,
  customerService: CustomerService,
  destroy$: Observable<void>,
  state: CustomerNameSearchState
): void {
  if (!name || name.trim().length < NAME_SEARCH_MIN_LENGTH) {
    state.nameSearchResults = [];
    state.showNameDropdown = false;
    return;
  }
  state.isSearchingCustomersByName = true;
  state.showNameDropdown = true;
  customerService.searchByName(name).pipe(takeUntil(destroy$)).subscribe({
    next: (customers) => {
      state.isSearchingCustomersByName = false;
      state.nameSearchResults = customers;
      state.showNameDropdown = customers.length > 0;
    },
    error: () => {
      state.isSearchingCustomersByName = false;
      state.nameSearchResults = [];
      state.showNameDropdown = false;
    }
  });
}

export interface CustomerSearchDropdownState {
  showNameDropdown: boolean;
  nameSearchResults: any[];
  showPhoneDropdown: boolean;
  phoneSearchResults: any[];
}

/**
 * Clear customer search dropdown state after selecting a customer.
 */
export function clearCustomerSearchDropdowns(state: CustomerSearchDropdownState): void {
  state.showNameDropdown = false;
  state.nameSearchResults = [];
  state.showPhoneDropdown = false;
  state.phoneSearchResults = [];
}

/**
 * Patch a customer form with search result. Supports different phone field names (e.g. 'phone' vs 'phoneNumber').
 */
export function patchCustomerFormFromSearchResult(
  form: FormGroup,
  customer: any,
  phoneFieldName: 'phone' | 'phoneNumber' = 'phone'
): void {
  form.patchValue({
    firstName: customer.first_name || '',
    lastName: customer.last_name || '',
    email: customer.email || '',
    [phoneFieldName]: customer.contact || ''
  }, { emitEvent: false });
}

/**
 * Setup debounced name and phone search subscriptions. Shared by add-customer-modal and customer-info.
 */
export function setupCustomerSearchSubscriptions(
  nameSearchSubject: Subject<string>,
  phoneSearchSubject: Subject<string>,
  destroy$: Observable<void>,
  searchByName: (name: string) => void,
  searchByPhone: (phoneNumber: string) => void
): void {
  nameSearchSubject.pipe(
    debounceTime(300),
    distinctUntilChanged(),
    takeUntil(destroy$)
  ).subscribe(name => searchByName(name));

  phoneSearchSubject.pipe(
    debounceTime(300),
    distinctUntilChanged(),
    takeUntil(destroy$)
  ).subscribe(phoneNumber => searchByPhone(phoneNumber));
}
